import React, { useEffect, useState } from 'react';
import { Modal } from 'react-bootstrap';

const COUNTDOWN_FROM = 10; // seconds — keep in sync with AUTO_HIDE_MS in useTableExport

/**
 * Renders the two export modals used by useTableExport:
 *  1. "Preparing" popup — shows a 10s countdown then auto-hides; the export keeps
 *     running in the background (and finishes even if the user navigates away).
 *  2. "Large export" (>10,000 records) decision popup — DOWNLOAD only.
 *     Copy never shows this.
 *
 * Pass the values straight from useTableExport.
 *
 * @param {boolean}  exporting   show the preparing popup
 * @param {Object}   prompt      truthy => show the Large export popup
 * @param {Function} onEmail     confirmEmail   (download + email)
 * @param {Function} onContinue  confirmContinue (download only)
 * @param {Function} onCancel    cancelPrompt
 * @param {string}   [loaderSrc] optional spinner gif (e.g. require('.../pinkloader.gif'))
 */
export default function ExportDialogs({
    exporting,
    prompt,
    onEmail,
    onContinue,
    onCancel,
    loaderSrc,
}) {
    // local countdown for display only — restarts each time the popup opens
    const [count, setCount] = useState(COUNTDOWN_FROM);
    
    useEffect(() => {
        if (!exporting) return undefined;
        setCount(COUNTDOWN_FROM);
        const id = setInterval(() => {
            setCount((c) => {
                if (c <= 1) {
                    clearInterval(id);
                    return 0;
                }
                return c - 1;
            });
        }, 1000);
        return () => clearInterval(id);
    }, [exporting]);

    return (
        <>
            {/* 1) Preparing popup (auto-hides after 10s, with countdown) ----- */}
            <Modal centered show={exporting} backdrop="static" keyboard={false}>
                <Modal.Body className="text-center py-4">
                    {loaderSrc ? (
                        <img style={{ width: 80 }} src={loaderSrc} alt="" />
                    ) : (
                        <div className="spinner-border text-secondary" role="status" />
                    )}
                    <h5 className="mt-3 mb-1">Preparing your request…</h5>
                    <p className="mb-1 text-muted">
                        This will keep running in the background. You can keep working —
                        your file will be ready shortly.
                    </p>
                    <p className="mb-0 fw-bold">Closing in {count}s</p>
                </Modal.Body>
            </Modal>

            {/* 2) Large export popup (>10,000 records — download only) ------- */}
            <Modal centered show={!!prompt} onHide={onCancel}>
                <Modal.Header closeButton>
                    <Modal.Title>Large export</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    The number of records is above 10,000, so downloading may take longer
                    than expected. Would you prefer to receive an email once the process is
                    completed instead?
                </Modal.Body>
                <Modal.Footer className='gap-2'>
                    <button className="registersendbtn1 my-1" style={{minWidth:"max-content"}} onClick={onContinue}>
                        Continue anyway
                    </button>
                    <button className="registersendbtn2 my-1" style={{minWidth:"max-content"}} onClick={onEmail}>
                        Email me instead
                    </button>
                </Modal.Footer>
            </Modal>
        </>
    );
}